import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Vault as VaultIcon, KeyRound, Loader2, Sparkles, Lock, Trophy,
  ArrowRight, Skull, Clock, Award, Zap, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BadgeStrip } from "@/components/BadgeStrip";
import "@/styles/vault.css";

// Map source_kind → emoji + plain-language description
const SOURCE_META = {
  daily_checkin: { emoji: "📅", label: "Daily Check-in", verb: "Check in today" },
  riddle:        { emoji: "🧩", label: "Riddle",          verb: "Solve the riddle" },
  qr_poster:     { emoji: "📍", label: "QR Poster",        verb: "Scan a poster" },
  waymark:       { emoji: "🗺️", label: "Find a Waymark",   verb: "Find the waymark" },
  mission:       { emoji: "❤️", label: "Mission",         verb: "Complete the mission" },
  drop_view:     { emoji: "🎁", label: "View Drop",        verb: "View the drop" },
  stash_streak:  { emoji: "🔥", label: "Stash Streak",     verb: "Maintain your streak" },
  sponsor_visit: { emoji: "🏪", label: "Visit Sponsor",    verb: "Visit the sponsor" },
  custom:        { emoji: "⚡", label: "Special",          verb: "Granted on the spot" },
};

// =====================================================================
// PUBLIC LIST PAGE — /vault
// =====================================================================
export function VaultsListPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/vault/today");
        if (!cancelled) setData(data || { items: [] });
      } catch (e) {
        if (!cancelled) setData({ items: [], reason: formatApiError(e.response?.data?.detail) || "Vault unavailable" });
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (busy) return <div className="py-20 text-center"><Loader2 size={28} className="inline animate-spin text-[#D7A62A]" /></div>;

  const items = data?.items || [];

  return (
    <div className="py-10 space-y-8" data-testid="vaults-list-page">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>citywide game layer</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.9]">
          The
          <span style={{ color: "#D7A62A" }}> Daily </span>
          Vault.
        </h1>
        <p className="mt-3 text-base text-[#F5F2E8]/75 max-w-xl mx-auto">
          One vault. One code. Built one fragment at a time, by what you do today.
          <br className="hidden md:block" />
          First hunter to crack it walks with the prize.
        </p>
      </header>

      {data?.reason && (
        <div className="max-w-2xl mx-auto loot-card-flat p-6 text-center" data-testid="vaults-list-reason">
          <Lock size={28} className="inline text-[#D7A62A] mb-2" />
          <div className="font-display uppercase text-xl">{data.reason}</div>
        </div>
      )}

      {items.length === 0 && !data?.reason && (
        <div className="max-w-2xl mx-auto loot-card-flat p-8 text-center" data-testid="vaults-list-empty">
          <VaultIcon size={32} className="inline text-[#D7A62A] mb-2" />
          <div className="font-display uppercase text-xl">No vault scheduled right now</div>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-2 max-w-md mx-auto">
            Check back soon — a new vault drops on the regular.
          </p>
        </div>
      )}

      <div className="max-w-3xl mx-auto space-y-3" data-testid="vaults-list">
        {items.map((v) => (
          <VaultListCard key={v.id} v={v} />
        ))}
      </div>

      <CityLeaderboard />
    </div>
  );
}

function VaultListCard({ v }) {
  const isBlack = v.is_black;
  return (
    <Link
      to={`/vault/${v.id}`}
      className="block loot-card-flat p-5 hover:border-[#D7A62A] transition-colors"
      style={{
        borderColor: isBlack ? "rgba(229,57,53,0.4)" : undefined,
        background: isBlack ? "rgba(229,57,53,0.04)" : undefined,
      }}
      data-testid={`vault-card-${v.id}`}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="p-3 border" style={{
          borderColor: isBlack ? "rgba(229,57,53,0.5)" : "rgba(215,166,42,0.5)",
          background: isBlack ? "rgba(229,57,53,0.06)" : "rgba(215,166,42,0.06)",
        }}>
          {isBlack ? <Skull size={28} style={{ color: "#E53935" }} /> : <VaultIcon size={28} style={{ color: "#D7A62A" }} />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display uppercase text-2xl leading-tight">
            {isBlack ? "🖤 The Black Vault" : v.title}
          </div>
          <div className="font-mono text-[11px] text-[#F5F2E8]/65 mt-1">
            {v.scope === "city" && v.city ? <>{v.city} only · </> : null}
            opens {v.opens_at?.slice(0, 16).replace("T", " ")} · status <strong>{v.status}</strong>
          </div>
          <div className="font-display text-base mt-2" style={{ color: "#76B82A" }}>
            🏆 {v.prize_label}
          </div>
          {v.description && <p className="text-xs text-[#F5F2E8]/75 mt-1">{v.description}</p>}
        </div>
        <ArrowRight size={20} className="text-[#D7A62A] self-center" />
      </div>
    </Link>
  );
}

// =====================================================================
// CITY LEADERBOARD
// =====================================================================
function CityLeaderboard() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/vault/leaderboard/cities")
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  const top = items[0]?.points || 1;
  return (
    <div className="max-w-3xl mx-auto loot-card-flat p-5 space-y-3" data-testid="vault-city-leaderboard">
      <div className="overline" style={{ color: "#76B82A" }}>City vs City</div>
      <p className="font-mono text-xs text-[#F5F2E8]/65 max-w-md">
        Every fragment claimed is a point for your city. The most active city earns sponsor boosts and bigger prizes.
      </p>
      <div className="space-y-2">
        {items.slice(0, 8).map((c, i) => (
          <div key={c.city} className="flex items-center gap-3" data-testid={`leaderboard-row-${i}`}>
            <span className="font-display text-xs w-6 text-right text-[#D7A62A]">#{i + 1}</span>
            <span className="font-display uppercase text-sm w-32 truncate">{c.city}</span>
            <div className="flex-1 city-bar">
              <i style={{ width: `${Math.max(8, Math.round((c.points / top) * 100))}%` }} />
            </div>
            <span className="font-mono text-xs text-[#F5F2E8]/65 w-16 text-right">{c.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// MAIN VAULT PAGE — /vault/:id
// =====================================================================
export default function VaultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(true);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [riddleFor, setRiddleFor] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [justGrantedPos, setJustGrantedPos] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get(`/vault/${id}`);
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Vault unavailable");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const claim = async (fragment, extra = {}) => {
    try {
      const { data } = await api.post(`/vault/${id}/claim`, {
        fragment_id: fragment.id,
        source_kind: fragment.source_kind,
        ...extra,
      });
      if (data.already_granted) {
        toast.info(`You already had position ${data.position + 1}.`);
      } else {
        toast.success(`+ "${data.char}" unlocked! (slot ${data.position + 1})`);
        setJustGrantedPos(data.position);
        setTimeout(() => setJustGrantedPos(null), 1200);
      }
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't claim");
    }
  };

  const submitCode = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/vault/${id}/attempt`, { code });
      if (data.correct && data.you_won) {
        toast.success("🏆 YOU WON! Heading to claim…");
        setShowConfetti(true);
        setTimeout(() => navigate("/my-prizes"), 2400);
      } else if (data.correct && data.you_were_too_late) {
        toast.error("Correct code, but someone beat you to it!");
        reload();
      } else if (data.correct) {
        toast.success("Correct!");
        reload();
      } else {
        toast.error("Not it. Try again.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (busy) return <div className="py-20 text-center"><Loader2 size={28} className="inline animate-spin text-[#D7A62A]" /></div>;
  if (!state?.vault) return <div className="py-20 text-center text-[#F5F2E8]/60">Vault not found.</div>;

  const v = state.vault;
  const isBlack = v.is_black;
  const isFinalized = v.status === "finalized";
  const themeColor = isBlack ? "#E53935" : v.theme_color || "#D7A62A";

  return (
    <div className="py-8 space-y-6" data-testid="vault-page">
      {/* Header / hero */}
      <div className={`vault-stage ${isBlack ? "is-black" : ""} max-w-4xl mx-auto`} style={{ position: "relative" }}>
        {showConfetti && <Confetti />}
        <div className="relative z-10 flex flex-col items-center text-center gap-4">
          <Countdown opensAt={v.opens_at} closesAt={v.closes_at} status={v.status} />
          <VaultArt isOpen={isFinalized} isBlack={isBlack} />
          <div>
            <div className="overline" style={{ color: themeColor }}>
              {isBlack ? "the black vault" : v.scope === "city" && v.city ? `${v.city} vault` : "today's vault"}
            </div>
            <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight mt-1 leading-[0.9] vault-title">
              {isBlack ? "🖤 BLACK VAULT" : v.title}
            </h1>
            <div className="font-display text-xl mt-2" style={{ color: "#76B82A" }}>🏆 {v.prize_label}</div>
            {v.description && <p className="font-mono text-xs text-[#F5F2E8]/75 mt-2 max-w-md mx-auto">{v.description}</p>}
            {v.sponsor_name && (
              <div className="flex items-center justify-center gap-2 mt-3 font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">
                {v.sponsor_logo_url && <img src={v.sponsor_logo_url} alt="" className="h-5 w-5 object-contain border border-[#F5F2E8]/15" />}
                Sponsored by {v.sponsor_name}
              </div>
            )}
          </div>

          {/* Code slot grid */}
          <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="vault-fragment-slots">
            {state.fragments.map((f) => {
              const granted = f.granted;
              const justGranted = justGrantedPos === f.position;
              const cls = granted ? "frag-chip is-revealed" : "frag-chip is-pending";
              return (
                <div
                  key={f.id}
                  className={cls + (justGranted ? " is-revealed" : "")}
                  data-testid={`vault-slot-${f.position}`}
                  data-granted={granted ? "1" : "0"}
                >
                  {granted ? f.char : "?"}
                </div>
              );
            })}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
            {state.my_progress}/{state.total_fragments} fragments collected
          </div>

          {/* Code entry — only when complete or admin */}
          {(state.complete || (state.fragments.length > 0)) && v.status === "active" && (
            <div className="w-full max-w-md mt-2" data-testid="vault-code-entry">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, v.code_length || 40))}
                placeholder={"•".repeat(v.code_length || 4)}
                className="vault-code-input"
                maxLength={v.code_length || 40}
                data-testid="vault-code-input"
              />
              <button
                onClick={submitCode}
                disabled={submitting || !code.trim()}
                className="btn-loot w-full text-sm mt-3 inline-flex items-center justify-center gap-2"
                data-testid="vault-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                {submitting ? "Cracking…" : "ENTER CODE"}
              </button>
              {!state.complete && (
                <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-2 text-center">
                  Tip: you can guess early if you think you know it.
                </p>
              )}
            </div>
          )}

          {isFinalized && (
            <div className="loot-card-flat p-4 mt-3 max-w-md w-full text-center border-2"
                 style={{ borderColor: "#76B82A", background: "rgba(118,184,42,0.06)" }}
                 data-testid="vault-finalized">
              <Trophy size={24} className="inline" style={{ color: "#76B82A" }} />
              <div className="font-display uppercase text-xl mt-1 inline-flex items-center justify-center gap-2 flex-wrap">
                Cracked by @{v.winner_username}
                <BadgeStrip username={v.winner_username} max={5} size={20} />
              </div>
              <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
                Code was <strong className="font-display tracking-widest">{state.fragments.map((f) => f.char || "?").join("")}</strong>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fragment cards (with claim CTAs) */}
      {!isFinalized && v.status === "active" && (
        <div className="max-w-3xl mx-auto space-y-3" data-testid="vault-fragment-cards">
          <div className="overline">How to earn fragments</div>
          {state.fragments.map((f) => {
            const meta = SOURCE_META[f.source_kind] || { emoji: "•", label: f.source_kind, verb: "Earn it" };
            return (
              <FragmentCard
                key={f.id}
                f={f}
                meta={meta}
                onClaim={() => {
                  if (f.source_kind === "riddle") setRiddleFor(f);
                  else claim(f);
                }}
              />
            );
          })}
        </div>
      )}

      <div className="text-center">
        <Link to="/vault" className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 hover:text-[#D7A62A]">
          ← all vaults
        </Link>
      </div>

      {riddleFor && (
        <RiddleModal
          fragment={riddleFor}
          onClose={() => setRiddleFor(null)}
          onSubmit={async (answer) => {
            try {
              await claim(riddleFor, { riddle_answer: answer });
              setRiddleFor(null);
            } catch {}
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function VaultArt({ isOpen, isBlack }) {
  const stroke = isBlack ? "#E53935" : "#D7A62A";
  const fill = isBlack ? "#1a0a0a" : "#1a1610";
  return (
    <svg
      viewBox="0 0 200 200"
      className="vault-svg"
      width="180"
      height="180"
      role="img"
      aria-label="Vault"
    >
      {/* Outer body */}
      <rect x="10" y="20" width="180" height="170" rx="10"
        fill={fill}
        stroke={stroke} strokeWidth="3" />
      {/* Bolts */}
      {[[24, 34], [176, 34], [24, 174], [176, 174]].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3" fill={stroke} />
      ))}
      {/* Door circle */}
      <circle cx="100" cy="105" r="58" fill={isOpen ? "#0a0a08" : fill} stroke={stroke} strokeWidth="3" />
      <circle cx="100" cy="105" r="50" fill="none" stroke={stroke} strokeOpacity="0.4" strokeWidth="1" />
      {/* Dial */}
      <g className="vault-dial" transform="translate(100 105)">
        <circle cx="0" cy="0" r="38" fill="none" stroke={stroke} strokeWidth="2" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return (
            <line key={i}
              x1={Math.cos(a) * 30} y1={Math.sin(a) * 30}
              x2={Math.cos(a) * 38} y2={Math.sin(a) * 38}
              stroke={stroke} strokeWidth="1.5" />
          );
        })}
        <circle cx="0" cy="0" r="6" fill={stroke} />
        <line x1="0" y1="0" x2="0" y2="-26" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* Handle */}
      <line x1="100" y1="50" x2="100" y2="35" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
      {/* Open glow */}
      {isOpen && (
        <g className="vault-burst">
          <circle cx="100" cy="105" r="40" fill={stroke} fillOpacity="0.18" />
          <circle cx="100" cy="105" r="20" fill={stroke} fillOpacity="0.25" />
        </g>
      )}
      {isBlack && (
        <text x="100" y="115" textAnchor="middle" fontFamily="serif" fontSize="32" fill={stroke} opacity="0.55">☠</text>
      )}
    </svg>
  );
}

function Countdown({ opensAt, closesAt, status }) {
  const target = status === "scheduled" ? opensAt : closesAt;
  const label = status === "scheduled" ? "OPENS IN" : status === "active" ? "ENDS IN" : "STATUS";
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target || status === "finalized" || status === "expired" || status === "cancelled") {
    return (
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
        <Clock size={11} className="inline mr-1" /> {status}
      </div>
    );
  }
  const ms = new Date(target).getTime() - now;
  if (ms <= 0) {
    return <div className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A]"><Zap size={11} className="inline mr-1" /> LIVE NOW</div>;
  }
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hh = Math.floor((totalSec % 86400) / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  return (
    <div className="font-mono text-[11px] uppercase tracking-widest text-[#F5F2E8]/85" data-testid="vault-countdown">
      <Clock size={11} className="inline mr-1" /> {label}{" "}
      <span className="font-display text-base ml-1">
        {days > 0 ? `${days}d ` : ""}{String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
      </span>
    </div>
  );
}

function FragmentCard({ f, meta, onClaim }) {
  const granted = f.granted;
  return (
    <div
      className="loot-card-flat p-3 flex items-start gap-3"
      style={{ opacity: granted ? 0.5 : 1 }}
      data-testid={`fragment-card-${f.id}`}
    >
      <div className="frag-source-tile">{f.icon_emoji || meta.emoji}</div>
      <div className="flex-1">
        <div className="font-display uppercase text-sm leading-tight">
          Slot #{f.position + 1} · <span className="text-[#F5F2E8]/65">{meta.label}</span>
        </div>
        <div className="font-mono text-[11px] text-[#F5F2E8]/65 mt-1">
          {f.hint || meta.verb}
        </div>
        {f.source_kind === "stash_streak" && f.min_streak > 0 && (
          <div className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
            Requires Stash streak ≥ {f.min_streak}
          </div>
        )}
      </div>
      {granted ? (
        <span className="font-display text-base" style={{ color: "#76B82A" }}>✓ {f.char}</span>
      ) : (
        <button
          onClick={onClaim}
          className="btn-loot text-xs"
          data-testid={`fragment-claim-${f.id}`}
          disabled={["sponsor_visit", "custom"].includes(f.source_kind)}
          title={["sponsor_visit", "custom"].includes(f.source_kind) ? "Granted on the spot" : "Claim now"}
        >
          {["sponsor_visit", "custom"].includes(f.source_kind) ? "On the spot" : "Claim"}
        </button>
      )}
    </div>
  );
}

function RiddleModal({ fragment, onClose, onSubmit }) {
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="loot-card-flat max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="overline" style={{ color: "#D7A62A" }}>riddle me this</div>
        <div className="font-display uppercase text-xl">{fragment.hint}</div>
        <p className="text-sm text-[#F5F2E8]/85">{fragment.riddle_question || "Type your answer:"}</p>
        <input
          className="loot-input"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          maxLength={200}
          placeholder="Your answer"
          autoFocus
          data-testid="vault-riddle-input"
        />
        <button
          onClick={async () => {
            setBusy(true);
            try { await onSubmit(answer); }
            finally { setBusy(false); }
          }}
          disabled={busy || !answer.trim()}
          className="btn-loot w-full text-xs"
          data-testid="vault-riddle-submit"
        >
          {busy ? "Checking…" : "Submit"}
        </button>
      </div>
    </div>
  );
}

function Confetti() {
  const colors = ["#D7A62A", "#76B82A", "#E53935", "#F5F2E8"];
  const pieces = useMemo(() => Array.from({ length: 50 }).map((_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    bg: colors[i % colors.length],
    rotate: Math.random() * 360,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), []);
  return (
    <div className="vault-confetti" aria-hidden>
      {pieces.map((p, i) => (
        <i key={i} style={{
          left: `${p.left}%`,
          background: p.bg,
          transform: `rotate(${p.rotate}deg)`,
          animationDelay: `${p.delay}s`,
        }} />
      ))}
    </div>
  );
}
